import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Lock, Save, Trash2, Globe2, GitBranch, Network } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { api } from "../../lib/api";
import { PermMatrix } from "./PermMatrix";
import { DEPT_COLORS } from "./OrgChart";
import { countGranted } from "../../lib/permGroups";

const SCOPES = [
  { id: "all", label: "كل الفروع", icon: Globe2 },
  { id: "branches", label: "فروعه المحددة", icon: GitBranch },
  { id: "department", label: "قسمه فقط", icon: Network },
];
const err = (e) => toast.error(e?.response?.data?.detail || "تعذّر الحفظ");

const RoleList = ({ ctx, sel, onSel, onNew }) => (
  <div className="space-y-1.5">
    <Button onClick={onNew} className="mb-2 w-full gap-1.5" data-testid="role-new-btn"><Plus className="h-4 w-4" /> دور جديد</Button>
    {ctx.roles.map((r) => {
      const n = ctx.users.filter((u) => u.role_id === r.id).length;
      return (
        <button key={r.id} type="button" onClick={() => onSel(r)} data-testid={`role-item-${r.id}`}
          className={`flex w-full items-center gap-2 rounded-xl border px-3 py-2.5 text-start transition-colors ${sel?.id === r.id ? "border-blue-500 bg-blue-50" : "border-slate-200 bg-white hover:border-slate-300"}`}>
          {r.system ? <Lock className="h-3.5 w-3.5 shrink-0 text-slate-400" /> : <span className="h-3 w-3 shrink-0 rounded-full" style={{ background: r.color }} />}
          <span className="flex-1 truncate text-sm font-semibold text-slate-800">{r.name}</span>
          <span className="text-[11px] text-slate-500">{n} مستخدم</span>
        </button>
      );
    })}
  </div>
);

export const RolesPanel = ({ ctx, t, reload }) => {
  const [sel, setSel] = useState(null);
  const [draft, setDraft] = useState(null);
  useEffect(() => { if (!sel && ctx.roles.length) setSel(ctx.roles[0]); }, [ctx.roles, sel]);
  useEffect(() => { if (sel) setDraft({ ...sel, perms: sel.perms || {} }); }, [sel]);
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const copyOf = (r) => setSel({ id: null, name: r ? `${r.name} (نسخة)` : "", color: DEPT_COLORS[4], scope: r?.scope || "branches", perms: r?.perms || { overview: { view: true } }, system: false });
  const save = async () => {
    const body = { name: draft.name, color: draft.color, scope: draft.scope, perms: draft.perms };
    try {
      const r = draft.id ? await api.orgEditRole(draft.id, body) : await api.orgAddRole(body);
      toast.success(draft.id ? "تم تحديث الدور وتطبيقه على مستخدميه" : "تم إنشاء الدور"); setSel(r); reload();
    } catch (e) { err(e); }
  };
  const saveSystem = async (perms) => {
    try {
      const r = await api.orgSetSystemRole(draft.id, perms);
      toast.success(perms ? `تم تحديث «${draft.name}»${r.users ? ` وتطبيقه على ${r.users} مستخدم` : ""}` : "تمت استعادة صلاحيات الدور الافتراضية");
      reload(); setSel(null);
    } catch (e) { err(e); }
  };
  const del = async () => { if (!window.confirm(`حذف دور «${draft.name}»؟`)) return; try { await api.orgDelRole(draft.id); setSel(null); reload(); } catch (e) { err(e); } };
  const sysEditable = draft?.system && draft.id !== "manager";
  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-[260px_1fr]" data-testid="roles-panel">
      <RoleList ctx={ctx} sel={sel} onSel={setSel} onNew={() => copyOf(null)} />
      {draft && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[220px] flex-1 space-y-1">
              <p className="text-xs font-semibold text-slate-500">اسم الدور</p>
              <Input value={draft.name} onChange={(e) => set("name", e.target.value)} disabled={draft.system} placeholder="مثال: مراجع حسابات" data-testid="role-name-input" />
            </div>
            {!draft.system && (
              <div className="flex gap-1.5 pb-2">{DEPT_COLORS.map((c) => (
                <button key={c} type="button" onClick={() => set("color", c)} className={`h-6 w-6 rounded-full transition-transform hover:scale-110 ${draft.color === c ? "ring-2 ring-slate-900 ring-offset-2" : ""}`} style={{ background: c }} />
              ))}</div>
            )}
          </div>
          <div className="space-y-1.5">
            <p className="text-xs font-semibold text-slate-500">ماذا يرى من الموظفين؟</p>
            <div className="flex flex-wrap gap-2">
              {SCOPES.map(({ id, label, icon: Icon }) => (
                <button key={id} type="button" disabled={draft.system} onClick={() => set("scope", id)} data-testid={`role-scope-${id}`}
                  className={`flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm transition-colors ${draft.scope === id ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-400"}`}>
                  <Icon className="h-3.5 w-3.5" /> {label}
                </button>
              ))}
            </div>
          </div>
          <p className="text-xs text-slate-500">{countGranted(draft.perms)} قسم متاح لهذا الدور</p>
          <PermMatrix value={draft.perms} onChange={draft.system && !sysEditable ? null : (p) => set("perms", p)} modules={ctx.modules} t={t} testid="role-matrix"
            lockedMsg="دور «مدير» يملك كل الصلاحيات دائماً" />
          <div className="flex flex-wrap gap-2">
            {draft.system
              ? sysEditable && <>
                <Button onClick={() => saveSystem(draft.perms)} className="gap-1.5" data-testid="role-save-btn"><Save className="h-4 w-4" /> حفظ الصلاحيات</Button>
                {draft.customized && <Button variant="ghost" onClick={() => saveSystem(null)} className="gap-1.5 text-slate-600" data-testid="role-reset-btn">استعادة الافتراضي</Button>}
              </>
              : <>
                <Button onClick={save} disabled={!draft.name?.trim()} className="gap-1.5" data-testid="role-save-btn"><Save className="h-4 w-4" /> حفظ الدور</Button>
                {draft.id && <Button variant="ghost" onClick={del} className="gap-1.5 text-red-600 hover:bg-red-50" data-testid="role-delete-btn"><Trash2 className="h-4 w-4" /> حذف</Button>}
              </>}
          </div>
        </div>
      )}
    </div>
  );
};
